import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import Home from "@/pages/Home";
import Courses from "@/pages/Courses";
import Admissions from "@/pages/Admissions";
import Process from "@/pages/Process";
import Tuition from "@/pages/Tuition";
import WhyEgos from "@/pages/WhyEgos";
import International from "@/pages/International";
import Demo from "@/pages/Demo";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import Checkout from "@/pages/Checkout";
import RefundPolicy from "@/pages/RefundPolicy";
import Admin from "@/pages/Admin";
import Seo, { SEO } from "@/components/Seo";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import "@/App.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  componentDidCatch(error) {
    console.error(error);
  }
  render() {
    if (this.state.err) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ivory text-center">
          <p className="font-display text-2xl font-bold text-ink">Something went wrong.</p>
          <Link to="/" className="btn-primary" data-testid="error-recovery-link">Back to homepage</Link>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      };
      const t1 = setTimeout(scrollToTarget, 150);
      const t2 = setTimeout(scrollToTarget, 900); // re-align after late images/layout shifts
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Chrome() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return !isAdmin ? (<><Navbar /><main><RoutesInner /></main><Footer /></>) : <RoutesInner />;
}

function SeoRoute({ path, Comp }) {
  return (
    <>
      {SEO[path] && <Seo {...SEO[path]} />}
      <Comp />
    </>
  );
}

function RoutesInner() {
  return (
    <Routes>
      <Route path="/" element={<SeoRoute path="/" Comp={Home} />} />
      <Route path="/courses" element={<SeoRoute path="/courses" Comp={Courses} />} />
      <Route path="/process" element={<SeoRoute path="/process" Comp={Process} />} />
      <Route path="/ossd" element={<Navigate to="/courses" replace />} />
      <Route path="/admissions" element={<SeoRoute path="/admissions" Comp={Admissions} />} />
      <Route path="/tuition" element={<SeoRoute path="/tuition" Comp={Tuition} />} />
      <Route path="/about" element={<Navigate to="/why-egos" replace />} />
      <Route path="/why-egos" element={<SeoRoute path="/why-egos" Comp={WhyEgos} />} />
      <Route path="/why-eoss" element={<Navigate to="/why-egos" replace />} />
      <Route path="/international" element={<SeoRoute path="/international" Comp={International} />} />
      <Route path="/testimonials" element={<Navigate to="/tuition" replace />} />
      <Route path="/faq" element={<SeoRoute path="/faq" Comp={Faq} />} />
      <Route path="/blog" element={<SeoRoute path="/blog" Comp={Blog} />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/contact" element={<SeoRoute path="/contact" Comp={Contact} />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/demo" element={<SeoRoute path="/demo" Comp={Demo} />} />
      <Route path="/privacy" element={<SeoRoute path="/privacy" Comp={PrivacyPolicy} />} />
      <Route path="/refund-policy" element={<SeoRoute path="/refund-policy" Comp={RefundPolicy} />} />
      <Route path="/checkout" element={<SeoRoute path="/checkout" Comp={Checkout} />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App min-h-screen bg-ivory">
          <Chrome />
          <BackToTop />
        </div>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </ErrorBoundary>
  );
}

export default App;
